import React from 'react';
import { colors, radius, typography } from '../../theme';

const OPTIONS = [60, 90, 120];

/**
 * The Cipher rest-timer moment (design audit item 11) — circular gold
 * progress instead of a plain countdown number. Duration stays a manual
 * choice (60/90/120s) rather than auto-starting: the user explicitly
 * prefers picking it themselves over automation (see
 * feedback_cipher_ui_manual_control memory) — this only upgrades the
 * presentation, not the interaction model.
 */
const RestTimer = ({ restSeconds, totalSeconds, onSelect, onClear }) => {
  if (restSeconds === null) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <span style={{ ...typography.label, fontSize: '11px', color: colors.textMuted }}>Rest</span>
        {OPTIONS.map((s) => (
          <button
            key={s}
            onClick={() => onSelect(s)}
            style={{
              padding: '6px 14px',
              border: `1px solid ${colors.borderStrong}`,
              borderRadius: radius.pill,
              background: 'transparent',
              color: colors.text,
              fontSize: '13px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {s === 120 ? '2m' : `${s}s`}
          </button>
        ))}
      </div>
    );
  }

  const done = restSeconds === 0;
  const r = 26;
  const circumference = 2 * Math.PI * r;
  const pct = totalSeconds > 0 ? Math.max(0, Math.min(1, (totalSeconds - restSeconds) / totalSeconds)) : 0;
  const dashoffset = circumference * (1 - pct);
  const mm = Math.floor(restSeconds / 60);
  const ss = String(restSeconds % 60).padStart(2, '0');

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
      <div style={{ position: 'relative', width: '60px', height: '60px', flexShrink: 0 }}>
        <svg width="60" height="60" viewBox="0 0 60 60" style={{ transform: 'rotate(-90deg)' }}>
          <circle cx="30" cy="30" r={r} fill="none" stroke={colors.surface3} strokeWidth="4" />
          <circle
            cx="30" cy="30" r={r} fill="none"
            stroke={done ? colors.success : colors.gold}
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashoffset}
            style={{ transition: 'stroke-dashoffset 1s linear' }}
          />
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {done ? (
            <span style={{ fontSize: '20px', color: colors.success }}>✓</span>
          ) : (
            <span style={{ fontSize: '13px', fontWeight: 700, color: colors.text, fontVariantNumeric: 'tabular-nums' }}>
              {mm}:{ss}
            </span>
          )}
        </div>
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ ...typography.label, fontSize: '11px', color: done ? colors.success : colors.textMuted }}>
          {done ? 'Rest done — go!' : 'Resting'}
        </div>
      </div>
      <button
        onClick={onClear}
        aria-label="Dismiss rest timer"
        style={{ background: 'none', border: 'none', color: colors.textMuted, fontSize: '20px', cursor: 'pointer', lineHeight: 1, padding: '4px' }}
      >
        ×
      </button>
    </div>
  );
};

export default RestTimer;
